<template>
    <div class="apprentice-simple-card">
        <div
            class="video-wrapper"
            :style="cssVars">
            <video
                class="azuremediaplayer amp-default-skin amp-big-play-centered"
                id="azuremediaplayer"
                ref="digivideo"
                tabindex="0"></video>

            <design-2021-digistudies-video-question-overlay-component
                v-if="canDisplayQuestion && currentDisplayedVideoQuestion"
                :question-in="currentDisplayedVideoQuestion"
                :correct-answer="correctAnswer"
                @check-answer="checkAnswers"
                @resume-video="resumeVideo">
            </design-2021-digistudies-video-question-overlay-component>
        </div>
        <div class="container-with-shadow">
            <div class="row">
                <div class="col-12">
                    <div
                        class="teacher"
                        v-if="video.teacher">
                        <span>Docent:</span> {{ video.teacher.first_name }} {{ video.teacher.last_name }}
                    </div>

                    <div class="description">
                        <b-tabs
                            class="apprentice-tab ds-video-detail-tab"
                            :style="cssVars"
                            active-tab-class="tab-active"
                            content-class="ds-video-detail-content"
                            nav-wrapper-class="nav-tabs"
                            pills>
                            <b-tab
                                class="video-description"
                                v-if="video.description"
                                :title="$t('digital_studies.concepts')">
                                <design-2021-apprentice-display-html-component :html-content="video.description">
                                </design-2021-apprentice-display-html-component>
                            </b-tab>

                            <b-tab
                                class="video-definitions"
                                v-if="video.definitions.length"
                                :title="$t('digital_studies.concepts')">
                                <div
                                    class="definition"
                                    v-for="definition in video.definitions">
                                    <span class="definition-word">{{ definition.name }}</span>
                                    <p class="definition-explanation">{{ definition.explanation }}</p>
                                </div>
                            </b-tab>

                            <b-tab
                                class="video-q-and-a"
                                v-if="video.exam_questions"
                                :title="$t('digital_studies.exam_questions')">
                                <design-2021-apprentice-display-html-component :html-content="video.exam_questions">
                                </design-2021-apprentice-display-html-component>

                                <b-button
                                    class="btn-toggle-answer"
                                    v-if="video.exam_answers"
                                    variant="success"
                                    @click="showAnswer = !showAnswer">
                                    <i
                                        class="far fa-eye-slash"
                                        v-if="showAnswer"></i>
                                    <i
                                        class="far fa-eye"
                                        v-else></i>
                                    {{
                                        !showAnswer
                                            ? $t('digital_studies.show_answer')
                                            : $t('digital_studies.hide_answer')
                                    }}
                                </b-button>

                                <div
                                    class="ds-video-answer"
                                    v-if="video.exam_answers && showAnswer">
                                    <h5 class="answer-title">{{ $t('digital_studies.correct_answer_is') }}</h5>
                                    <design-2021-apprentice-display-html-component :html-content="video.exam_answers">
                                    </design-2021-apprentice-display-html-component>
                                </div>
                            </b-tab>
                        </b-tabs>

                        <div
                            class="ds_video-improvements"
                            v-if="showButton">
                            <b-button
                                class="btn-graduation-cap btn-improvements"
                                :href="routes.baseUrl() + '/videos/video/improve/' + video.id"
                                variant="primary">
                                {{ $t('digital_studies.correct_answer_is') }}
                            </b-button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { CommonBus } from '../../app';

export default {
    name: 'design-2021-digistudies-video',
    props: ['video', 'token', 'discipline', 'questionsIn'],
    data() {
        return {
            showButton: AuthIsAdmin || AuthIsTeacher,
            dsVideoPlayer: '',
            questions: this.questionsIn.map((question) => {
                return {
                    ...question,
                    can_show: true,
                };
            }),
            showAnswer: false,
            duration: null,
            selectedAnswer: null,
            correctAnswer: null,
            currentDisplayedVideoQuestion: null,
            canDisplayQuestion: false,
            windowWidth: 0,
        };
    },

    created() {
        window.addEventListener('resize', this.handleResize);
        this.handleResize();
    },

    destroyed() {
        window.removeEventListener('resize', this.handleResize);
    },

    mounted() {
        const myOptions = {
            autoplay: true,
            controls: true,
            width: '640',
            height: '400',
            fluid: true,
            poster: this.video.thumbnail_url,
        };
        const self = this;

        this.dsVideoPlayer = amp('azuremediaplayer', myOptions);
        this.dsVideoPlayer.src([
            {
                src: this.video.stream_url,
                streamingFormats: ['DASH', 'SMOOTH'],
                type: 'application/vnd.ms-sstr+xml',
                protectionInfo: [{ type: 'AES', authenticationToken: 'Bearer=' + this.token }],
            },
            {
                src:
                    'https://digistudies.azurewebsites.net/v1/videos/hls-load/' +
                    this.video.id +
                    '?token=' +
                    this.token,
                type: 'application/vnd.apple.mpegurl',
                streamingFormats: ['HLS-V4', 'HLS-V3'],
            },
        ]);

        this.dsVideoPlayer.addEventListener('timeupdate', function () {
            self.duration = this.currentAbsoluteTime();
        });
    },

    computed: {
        cssVars() {
            return {
                '--bg-color': this.discipline.color,
            };
        },
    },

    watch: {
        duration: function (value) {
            if (this.windowWidth >= 576) {
                // kijken of er een vraag is die bij deze timestamp hoort.
                const questionForTimestamp = this.questions.find(
                    (question) => question.track_seconds === parseInt(value),
                );

                // als er een vraag gevonden is, kijken of die getoond mag worden. Dit in het geval dat we niet vast
                // komen te zitten in dezelfde timestamp wanneer we de video weer verder spelen na dat de vraag is
                // beantwoord.
                if (questionForTimestamp && questionForTimestamp.can_show) {
                    questionForTimestamp.can_show = false;
                    this.selectedAnswer = null;
                    this.correctAnswer = null;
                    this.currentDisplayedVideoQuestion = questionForTimestamp;
                    this.canDisplayQuestion = true;
                    this.dsVideoPlayer.pause();
                    return;
                }

                // we have to check if we have to reset some can_show booleans in case the question needs to be opened
                // again
                for (let question of this.questions) {
                    if (question.can_show) {
                        continue;
                    }

                    if (value < question.track_seconds - 1.2 || value > question.track_seconds + 1.2) {
                        question.can_show = true;
                        this.currentDisplayedVideoQuestion = null;
                        this.canDisplayQuestion = false;
                    }
                }
            }
        },
    },

    methods: {
        checkAnswers() {
            this.correctAnswer = this.currentDisplayedVideoQuestion.answers.filter((answer) => answer.is_correct);
        },

        resumeVideo() {
            this.questions = this.questions.map((question) => {
                if (question.id === this.currentDisplayedVideoQuestion.id) {
                    question.can_show = false;
                }
                return question;
            });
            this.currentDisplayedVideoQuestion = null;
            this.canDisplayQuestion = false;
            this.dsVideoPlayer.play();
        },

        handleResize() {
            this.windowWidth = window.innerWidth;
        },
    },
};
</script>

<style scoped lang="scss">
.azuremediaplayer-dimensions {
    width: 100%;
    min-height: 215px;
    height: auto;
}

.teacher {
    margin-top: $grid-gutter-width;
    margin-bottom: map-get($grid-gutter-spacing, 'fifteen');

    span {
        font-weight: $font-weight-bold;
    }
}

.ds-video-detail-tab ::v-deep {
    .nav-pills {
        a {
            color: var(--bg-color);

            &:hover,
            &.active {
                color: var(--bg-color);

                &:after {
                    background-color: var(--bg-color);
                }
            }
        }
    }

    .ds-video-detail-content {
        height: fit-content;
        padding: 0 map-get($grid-gutter-spacing, 'twenty-one') map-get($grid-gutter-spacing, 'twenty-one')
            map-get($grid-gutter-spacing, 'twenty-one');
    }
}

.btn-toggle-answer {
    margin-top: map-get($grid-gutter-spacing, 'forty-two');
    margin-bottom: map-get($grid-gutter-spacing, 'forty-two');
    color: $white;

    i {
        margin-right: map-get($grid-gutter-spacing, 'eight');
    }

    &,
    &:hover,
    &:active,
    &:not(:disabled):not(.disabled):active {
        border-color: var(--bg-color);
        background-color: var(--bg-color);
        box-shadow: 0 0 0 0.2rem rgba(var(--bg-color), 0.5);
    }
}

.ds-video-answer {
    .answer-title {
        margin-bottom: map-get($grid-gutter-spacing, 'fifteen');
    }
}

.ds_video-improvements {
    margin-top: map-get($grid-gutter-spacing, 'forty-two');

    .btn {
        width: 100%;
    }
}

.tab-content {
    padding-top: map-get($grid-gutter-spacing, 'fifteen');
}

.definition {
    padding-bottom: map-get($grid-gutter-spacing, 'ten');
    margin-bottom: map-get($grid-gutter-spacing, 'ten');
    display: flex;
    flex-wrap: wrap;
    border-bottom: 1px solid $border-color;

    .definition-word {
        font-size: $base-font-size;
        font-weight: $font-weight-medium;
        box-sizing: border-box;
        word-break: break-word;
    }

    .definition-explanation {
        width: 100%;
        margin-bottom: 0;
    }
}

@media (min-width: map-get($grid-breakpoints, 'sm')) {
    .ds_video-improvements {
        display: flex;
        align-items: center;
        justify-content: center;

        .btn {
            width: auto;
        }
    }

    .video-wrapper {
        position: relative;

        .question {
            display: flex;
            align-items: flex-start;
            justify-content: center;
            flex: 1 0 100%;
            flex-wrap: wrap;
            position: absolute;
            background-color: var(--bg-color);
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
            opacity: 0.93;
            text-align: center;
            color: $white;
            padding: map-get($grid-gutter-spacing, 'fifteen') map-get($grid-gutter-spacing, 'fifteen') 45px
                map-get($grid-gutter-spacing, 'ten');
        }

        .question-wrapper {
            position: relative;
            width: 100%;
        }

        .question-title {
            font-size: $base-font-size;
            font-weight: $font-weight-bold;
            margin-bottom: map-get($grid-gutter-spacing, 'twenty-one');
        }

        .multiple-choice-answer ::v-deep {
            flex: 1 0 100%;

            legend {
                display: none;

                & + div {
                    text-align: left;
                    width: fit-content;
                    margin: auto;
                }
            }

            .answer-option {
                &:not(:last-child) {
                    margin-bottom: map-get($grid-gutter-spacing, 'eight');
                }
            }

            label {
                font-size: $default-font-size;

                &:after,
                &:before {
                    top: 0;
                }
            }

            .btn {
                font-size: $default-font-size;
            }
        }

        .multiple-choice-answer.checked {
            .answer-option {
                &.correct ::v-deep {
                    color: $primary;

                    label {
                        &:before,
                        &:after {
                            font-family: 'Font Awesome 5 Pro';
                            width: 1.25em;
                            font-size: $base-font-size;
                            border: none;
                            background-color: unset;
                        }

                        &:before {
                            content: '\f0c8';
                            font-size: 1.125rem;
                            color: $white;
                        }

                        &:after {
                            content: '\f00c';
                            font-size: $base-font-size;
                            color: $primary;
                            top: 1px;
                        }
                    }
                }

                &.answer:not(.correct) ::v-deep {
                    color: $danger;

                    label {
                        &:before,
                        &:after {
                            font-family: 'Font Awesome 5 Pro';
                            width: 1.25em;
                            font-size: $base-font-size;
                            border: none;
                            background-color: unset;
                        }

                        &:before {
                            content: '\f0c8';
                            font-size: 1.125rem;
                            color: $white;
                        }

                        &:after {
                            content: '\f00d';
                            font-size: $base-font-size;
                            color: $danger;
                            top: 1px;
                            left: -22px;
                        }
                    }
                }
            }
        }

        .btn {
            font-size: $default-font-size;
        }

        .btn-check-answer {
            margin-left: $grid-gutter-width;
        }
    }
}

@media (min-width: map-get($grid-breakpoints, 'md')) {
    .azuremediaplayer-dimensions {
        height: 400px;
    }

    .definition {
        flex-wrap: nowrap;

        .definition-word {
            flex: 1 0 45%;
            padding-right: map-get($grid-gutter-spacing, 'forty-two');
            text-align: right;
        }
    }

    .definition-word {
        display: inline;
        width: 25%;
        justify-content: flex-end;
    }

    .definition-explanation {
        width: 75%;
    }
}

@media (min-width: map-get($grid-breakpoints, 'lg')) {
    .azuremediaplayer-dimensions {
        height: 550px;
    }

    .video-wrapper {
        .question-wrapper {
            top: 15%;
        }

        .question-title {
            margin-bottom: map-get($grid-gutter-spacing, 'twenty-two');
        }
    }
}

@media (min-width: map-get($grid-breakpoints, 'xl')) {
    .definition {
        .definition-word {
            flex: 1 0 33%;
        }
    }
}
</style>
